import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Slider } from "../components/ui/slider";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Settings2,
  Loader2,
  ArrowUpDown,
  ImageDown,
  Save
} from "lucide-react";

export default function ParametersPage() {
  const { token } = useAuth();
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingParam, setEditingParam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    unit: "",
    min_value: 0,
    max_value: 100,
    description: ""
  });

  // Compression settings state
  const [compressionSettings, setCompressionSettings] = useState({
    max_width: 1920,
    max_height: 1080,
    quality: 0.85,
    max_size_kb: 500
  });
  const [savingCompression, setSavingCompression] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchParameters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parameters`, { headers });
      setParameters(response.data);
    } catch (error) {
      toast.error("Failed to load parameters");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchCompressionSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/compression`, { headers });
      setCompressionSettings(response.data);
    } catch (error) {
      console.error("Failed to load compression settings");
    }
  }, [headers]);

  const saveCompressionSettings = async () => {
    setSavingCompression(true);
    try {
      await axios.put(`${API}/settings/compression`, compressionSettings, { headers });
      toast.success("Compression settings saved");
    } catch (error) {
      toast.error("Failed to save compression settings");
    } finally {
      setSavingCompression(false);
    }
  };

  useEffect(() => {
    fetchParameters();
    fetchCompressionSettings();
  }, [fetchParameters, fetchCompressionSettings]);

  const handleOpenDialog = (param = null) => {
    if (param) {
      setEditingParam(param);
      setFormData({
        name: param.name,
        unit: param.unit,
        min_value: param.min_value,
        max_value: param.max_value,
        description: param.description || ""
      });
    } else {
      setEditingParam(null);
      setFormData({
        name: "",
        unit: "",
        min_value: 0,
        max_value: 100,
        description: ""
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Parameter name is required");
      return;
    }
    if (!formData.unit.trim()) {
      toast.error("Unit is required");
      return;
    }
    if (formData.min_value >= formData.max_value) {
      toast.error("Min value must be less than max value");
      return;
    }

    setSubmitting(true);
    try {
      if (editingParam) {
        await axios.put(`${API}/parameters/${editingParam.id}`, formData, { headers });
        toast.success("Parameter updated");
      } else {
        await axios.post(`${API}/parameters`, formData, { headers });
        toast.success("Parameter created");
      }
      setShowDialog(false);
      fetchParameters();
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this parameter?")) return;
    
    try {
      await axios.delete(`${API}/parameters/${id}`, { headers });
      toast.success("Parameter deleted");
      fetchParameters();
    } catch (error) {
      toast.error("Failed to delete parameter");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parameters-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Parameters</h2>
          <p className="text-muted-foreground">Define measurement parameters and their valid ranges</p>
        </div>
        
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-parameter-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Parameter
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle>{editingParam ? "Edit Parameter" : "Add New Parameter"}</DialogTitle>
              <DialogDescription>
                Define a measurement parameter with its acceptable range
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Parameter Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Chlorine, pH, Turbidity"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="param-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  placeholder="e.g., mg/L, pH, NTU"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  data-testid="param-unit-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_value">Minimum Value</Label>
                  <Input
                    id="min_value"
                    type="number"
                    step="any"
                    value={formData.min_value}
                    onChange={(e) => setFormData({ ...formData, min_value: parseFloat(e.target.value) })}
                    className="font-mono"
                    data-testid="param-min-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_value">Maximum Value</Label>
                  <Input
                    id="max_value"
                    type="number"
                    step="any"
                    value={formData.max_value}
                    onChange={(e) => setFormData({ ...formData, max_value: parseFloat(e.target.value) })}
                    className="font-mono"
                    data-testid="param-max-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this parameter measures..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  data-testid="param-description-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-param-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingParam ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Parameters Table */}
      <Card>
        <CardContent className="p-0">
          {parameters.length === 0 ? (
            <div className="text-center py-12">
              <Settings2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No parameters defined yet</p>
              <p className="text-sm text-muted-foreground">Add parameters to define valid measurement ranges</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Parameter</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Range
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parameters.map((param) => (
                  <TableRow key={param.id} data-testid={`param-row-${param.id}`}>
                    <TableCell className="font-medium">{param.name}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {param.unit}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-mono text-sm">
                        <span className="text-emerald-600">{param.min_value}</span>
                        <span className="text-muted-foreground">-</span>
                        <span className="text-red-600">{param.max_value}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                      {param.description || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(param)}
                          data-testid={`edit-param-${param.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(param.id)}
                          data-testid={`delete-param-${param.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference Card */}
      {parameters.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Parameter Ranges Overview</CardTitle>
            <CardDescription>Quick reference for all defined parameters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {parameters.map((param) => (
                <div key={param.id} className="p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{param.name}</span>
                    <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{param.unit}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-red-500"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs font-mono text-muted-foreground">
                    <span>{param.min_value}</span>
                    <span>{param.max_value}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Compression Settings */}
      <Card data-testid="compression-settings-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ImageDown className="w-5 h-5 text-blue-500" />
            Image Compression Settings
          </CardTitle>
          <CardDescription>Configure how images are compressed before upload to optimize performance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="max_width">Max Width (px)</Label>
              <Input
                id="max_width"
                type="number"
                min={640}
                max={3840}
                step={10}
                value={compressionSettings.max_width}
                onChange={(e) => setCompressionSettings({ ...compressionSettings, max_width: parseInt(e.target.value) || 1920 })}
                className="font-mono"
                data-testid="compression-max-width"
              />
              <p className="text-xs text-muted-foreground">Images wider than this will be scaled down</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_height">Max Height (px)</Label>
              <Input
                id="max_height"
                type="number"
                min={480}
                max={2160}
                step={10}
                value={compressionSettings.max_height}
                onChange={(e) => setCompressionSettings({ ...compressionSettings, max_height: parseInt(e.target.value) || 1080 })}
                className="font-mono"
                data-testid="compression-max-height"
              />
              <p className="text-xs text-muted-foreground">Images taller than this will be scaled down</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>JPEG Quality</Label>
              <span className="text-sm font-mono font-medium" data-testid="compression-quality-value">
                {Math.round(compressionSettings.quality * 100)}%
              </span>
            </div>
            <Slider
              value={[compressionSettings.quality * 100]}
              onValueChange={([val]) => setCompressionSettings({ ...compressionSettings, quality: val / 100 })}
              min={30}
              max={100}
              step={5}
              data-testid="compression-quality-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Smaller file size</span>
              <span>Higher quality</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max_size_kb">Max File Size (KB)</Label>
            <Input
              id="max_size_kb"
              type="number"
              min={100}
              max={5000}
              step={50}
              value={compressionSettings.max_size_kb}
              onChange={(e) => setCompressionSettings({ ...compressionSettings, max_size_kb: parseInt(e.target.value) || 500 })}
              className="font-mono"
              data-testid="compression-max-size"
            />
            <p className="text-xs text-muted-foreground">Target maximum file size after compression (quality may be reduced further to meet this)</p>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-sm text-muted-foreground">
              Current: <span className="font-mono">{compressionSettings.max_width}x{compressionSettings.max_height}</span> at <span className="font-mono">{Math.round(compressionSettings.quality * 100)}%</span> quality, max <span className="font-mono">{compressionSettings.max_size_kb}KB</span>
            </div>
            <Button onClick={saveCompressionSettings} disabled={savingCompression} data-testid="save-compression-btn">
              {savingCompression ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
